import { getWorkDayEndMs } from './workSchedule';

export const ACTIVE_STATUSES = ['En curso'];
export const FINISHED_STATUSES = ['Completado', 'Finalizado', 'Cancelado'];

export const PRODUCTIVITY_ROLES = {
  mechanic: {
    key: 'mechanic',
    storagePrefix: 'service-productivity-v1',
    startField: 'serviceStart',
    endField: 'serviceEnd',
    totalField: 'totalTime',
  },
  assistant: {
    key: 'assistant',
    storagePrefix: 'assistant-productivity-v1',
    startField: 'assistantStart',
    endField: 'assistantEnd',
    totalField: 'assistantTotalTime',
  },
};

const resolveRole = (role = 'mechanic') =>
  PRODUCTIVITY_ROLES[role] || PRODUCTIVITY_ROLES.mechanic;

const storageKey = (vehicleServiceId, roleConfig) =>
  `${roleConfig.storagePrefix}-${vehicleServiceId}`;

const emptyState = (vehicleServiceId) => ({
  version: 1,
  vehicleServiceId,
  productiveMs: 0,
  running: false,
  segmentStartedAt: null,
  finalized: false,
  wasPaused: false,
});

/** Milisegundos productivos del tramo actual, sin contar fuera de horario (después de las 16:00). */
export const getSegmentElapsedMs = (segmentStartedAt, nowMs = Date.now()) => {
  if (!segmentStartedAt) return 0;
  const workEnd = getWorkDayEndMs(segmentStartedAt);
  const effectiveEnd = Math.min(nowMs, workEnd);
  if (effectiveEnd <= segmentStartedAt) return 0;
  return effectiveEnd - segmentStartedAt;
};

/** Si el tramo quedó corriendo más allá del cierre (16:00) o al día siguiente, pausar al cierre. */
export const settleRunningState = (state, nowMs = Date.now()) => {
  if (!state?.running || !state.segmentStartedAt) return state;
  const workEnd = getWorkDayEndMs(state.segmentStartedAt);
  if (nowMs >= workEnd) {
    return pauseTimer(state, workEnd);
  }
  return state;
};

export const loadTimerState = (
  vehicleServiceId,
  vehicleService,
  appointmentStatus,
  role = 'mechanic',
) => {
  const roleConfig = resolveRole(role);
  if (!vehicleServiceId) return emptyState(vehicleServiceId);

  const isActive = ACTIVE_STATUSES.includes(appointmentStatus);
  const isFinished = FINISHED_STATUSES.includes(appointmentStatus);
  const startValue = vehicleService?.[roleConfig.startField];
  const endValue = vehicleService?.[roleConfig.endField];
  const totalValue = vehicleService?.[roleConfig.totalField];

  const raw = localStorage.getItem(storageKey(vehicleServiceId, roleConfig));
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.version === 1) {
        if (isActive) {
          return settleRunningState({
            ...parsed,
            vehicleServiceId,
            finalized: false,
          });
        }
        if (isFinished) {
          return {
            ...parsed,
            finalized: true,
            running: false,
            segmentStartedAt: null,
          };
        }
        return parsed;
      }
    } catch {
      /* use fallback */
    }
  }

  const state = emptyState(vehicleServiceId);

  if (isFinished && startValue) {
    if (totalValue != null && totalValue > 0) {
      state.productiveMs = totalValue * 60 * 1000;
    } else {
      const start = new Date(startValue).getTime();
      const end = endValue ? new Date(endValue).getTime() : Date.now();
      state.productiveMs = Math.max(0, end - start);
    }
    state.finalized = true;
    state.running = false;
    return state;
  }

  if (isActive) {
    const productiveMs =
      totalValue != null && totalValue > 0 ? totalValue * 60 * 1000 : 0;
    return {
      ...state,
      productiveMs,
      running: false,
      segmentStartedAt: null,
      finalized: false,
      wasPaused: productiveMs > 0,
    };
  }

  return state;
};

export const saveTimerState = (state, role = 'mechanic') => {
  if (!state?.vehicleServiceId) return;
  const roleConfig = resolveRole(role);
  localStorage.setItem(
    storageKey(state.vehicleServiceId, roleConfig),
    JSON.stringify(state),
  );
};

export const clearTimerState = (vehicleServiceId, role = 'mechanic') => {
  if (!vehicleServiceId) return;
  const roleConfig = resolveRole(role);
  localStorage.removeItem(storageKey(vehicleServiceId, roleConfig));
};

export const getElapsedSeconds = (state, nowMs = Date.now()) => {
  if (!state) return 0;
  let ms = state.productiveMs || 0;
  if (state.running && state.segmentStartedAt) {
    ms += getSegmentElapsedMs(state.segmentStartedAt, nowMs);
  }
  return Math.floor(ms / 1000);
};

export const startTimer = (state) => {
  if (!state || state.finalized) return state;
  if (state.running) return state;
  return {
    ...state,
    running: true,
    segmentStartedAt: Date.now(),
    finalized: false,
    wasPaused: false,
  };
};

export const pauseTimer = (state, atMs = Date.now()) => {
  if (!state || state.finalized || !state.running) return state;
  const segmentMs = state.segmentStartedAt
    ? getSegmentElapsedMs(state.segmentStartedAt, atMs)
    : 0;
  return {
    ...state,
    productiveMs: (state.productiveMs || 0) + segmentMs,
    running: false,
    segmentStartedAt: null,
    wasPaused: true,
  };
};

export const resumeTimer = (state) => {
  if (!state || state.finalized) return state;
  return startTimer({ ...state, running: false, segmentStartedAt: null });
};

/** Pausar y marcar como cerrado (finalizar / cancelar servicio) */
export const finalizeTimer = (state) => {
  if (!state) return state;
  const next = pauseTimer(state);
  return { ...next, finalized: true, running: false, segmentStartedAt: null };
};

export const productiveSecondsToMinutes = (seconds) =>
  Math.max(1, Math.round(seconds / 60));

export const buildSavePayload = (
  state,
  { startValue, endValue, role = 'mechanic' } = {},
) => {
  const roleConfig = resolveRole(role);
  const seconds = getElapsedSeconds(state);
  const now = new Date();
  return {
    [roleConfig.totalField]:
      seconds > 0 ? productiveSecondsToMinutes(seconds) : 0,
    productiveSeconds: seconds,
    [roleConfig.startField]: startValue || null,
    [roleConfig.endField]: state?.finalized ? endValue || now : null,
  };
};

/** Solo arrancar automáticamente al pasar a "En curso" por primera vez (no si estaba pausado). */
export const shouldAutoStart = (state) => {
  if (!state || state.finalized || state.running) return false;
  if (state.wasPaused) return false;
  if ((state.productiveMs || 0) > 0 && !state.running) return false;
  return true;
};
